import type { OrganizationType } from '@form-factory/schema';
import {
  createOrganizationSchema,
  updateOrganizationSchema,
} from '@form-factory/schema/contracts';
import { Role, Currency } from '@form-factory/schema/enums';
import { formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Button, cn } from '@utima/ui';
import { Pencil } from 'lucide-react';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { MetaFields } from '@/components/fields/MetaFields';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { Protected } from '@/components/protected/Protected';
import { useUser } from '@/hooks';
import { trpc } from '@/services/trpc';

import { OrganizationBillingInfo } from './organizationBillingInfo/OrganizationBillingInfo';
import { TierConfigFields } from './TierConfigFields';
// import { WorkingHours } from './workingHours/WorkingHours';
import { useOrganizationMutation } from '../hooks/useOrganizationMutation';

export type OrganizationFormProps = {
  data?: OrganizationType;
  organizationId?: string;
};

export const OrganizationForm = memo(function OrganizationForm({
  data,
}: OrganizationFormProps) {
  const { t } = useLingui();
  const orgCurrency = data?.currency ?? Currency.USD;
  const { user, currentOrganizationId, setUser } = useUser();
  const {
    create: { mutateAsync: createOrg },
    remove: { mutateAsync: removeOrg },
    update: { mutateAsync: updateOrg },
  } = useOrganizationMutation();

  const { refetch: refetchUser } = trpc.users.me.useQuery(undefined, {
    enabled: false,
  });

  // Fetch active subscription data to display auto-renewal information
  const { data: activeSubscription, isLoading: isLoadingSubscription } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId: currentOrganizationId! },
      { enabled: !!currentOrganizationId },
    );

  const { schema: orgSettingsSchema } = useFieldsSchema(
    [
      {
        name: 'margin',
        label: t`Margin`,
        type: 'number',
        uiProps: {
          step: '0.01',
          min: '1',
        },
        defaultValue: 5,
        tooltip: t`Multiplier applied to base usage costs (e.g., 1.5 = 50% markup). Minimum 1.`,
        required: true,
      },
      {
        name: 'currency',
        label: t`Billing Currency`,
        type: 'select',
        defaultValue: data?.currency ?? Currency.CZK,
        options: {
          [Currency.USD]: 'USD ($)',
          [Currency.CZK]: 'CZK (Kč)',
        },
        required: true,
        tooltip: t`Select the primary currency for billing and balance for this organization.`,
      },
    ],
    [t, data?.currency],
  );

  if (!currentOrganizationId) {
    throw new Error('No current organization id');
  }

  const current = data?.currentMonthlyUsage ?? 0;
  const limit = data?.monthlyUsageLimit ?? 0;

  // Determine if this is a Form Factory plan (0 allowance, balance-only model)
  const isFormFactoryPlan = limit === 0 && !!data?.id;

  // Get auto-renewal information
  const autoRenewalCharged =
    activeSubscription?.autoRenewalChargedThisPeriod ?? 0;
  const autoRenewalLimit = activeSubscription?.autoRenewalLimit ?? 0;
  const hasAutoRenewalLimit =
    activeSubscription?.autoRenewal &&
    typeof activeSubscription.autoRenewalLimit === 'number' &&
    activeSubscription.autoRenewalLimit > 0;

  return (
    <EntityCRUDForm
      initialData={data}
      createSchema={createOrganizationSchema}
      updateSchema={updateOrganizationSchema}
      onCreate={async values => {
        const result = await createOrg(values);

        return result.organization as unknown as OrganizationType;
      }}
      onUpdate={async values => {
        const marginUpdate =
          user?.role &&
          user.role === Role.SuperAdmin &&
          values.margin !== undefined
            ? { margin: values.margin }
            : {};
        const updateValues = { ...values, ...marginUpdate };
        const updatedValue = await updateOrg(updateValues);

        if (updatedValue.id === currentOrganizationId) {
          const updatedUser = await refetchUser();
          if (updatedUser.data) {
            setUser(updatedUser.data);
          }
        }

        return updatedValue;
      }}
      onRemove={async ({ id }) => {
        await removeOrg({ id });
      }}
      basePath='/organizations'
      idParamName='organizationId'
      deleteDescription={t`Are you sure you want to delete this organization? Any remaining credits will be permanently lost.`}
      customRedirect='/inbox'
      confirmationText={data?.name}
      confirmationLabel={t`Type the organization name to confirm:`}
    >
      <EntityCRUDForm.Col>
        <TierConfigFields />
        <Protected roles={[Role.SuperAdmin]}>
          <Card title={t`Billing Settings`} className='mt-4 space-y-1'>
            <SchemaFields schema={orgSettingsSchema} />
          </Card>
        </Protected>
      </EntityCRUDForm.Col>

      {/* Hidden for FF now */}
      {/* <EntityCRUDForm.Col>
        <WorkingHours name='workingHours' />
      </EntityCRUDForm.Col> */}

      <EntityCRUDForm.Col>
        <MetaFields data={data}>
          {/* Display different information based on plan type */}
          {isFormFactoryPlan ? (
            <>
              <DisplayValue label={t`Remaining Balance`}>
                <span className='font-semibold text-green-600'>
                  {formatUtils.credits(data?.balance ?? 0, orgCurrency)}
                </span>
              </DisplayValue>
              <DisplayValue label={t`Auto-renewal limit`}>
                {isLoadingSubscription ? (
                  <span className='text-gray-500'>Loading...</span>
                ) : hasAutoRenewalLimit ? (
                  <span
                    className={cn(
                      'inline-flex items-baseline gap-1',
                      autoRenewalCharged >= autoRenewalLimit
                        ? 'text-red-600'
                        : 'text-green-600',
                    )}
                  >
                    <span className='font-semibold'>
                      {formatUtils.credits(autoRenewalCharged, orgCurrency)}
                    </span>
                    /
                    <span className='text-xs font-semibold'>
                      {formatUtils.credits(autoRenewalLimit, orgCurrency)}
                    </span>
                    <span className='ml-1 text-xs font-normal text-gray-500'>
                      (this period)
                    </span>
                  </span>
                ) : (
                  <span className='text-gray-500'>
                    {activeSubscription?.autoRenewal
                      ? t`No spending limit set`
                      : t`Auto-renewal disabled`}
                  </span>
                )}
              </DisplayValue>
            </>
          ) : (
            <>
              <DisplayValue label={t`Prepaid balance`}>
                <span
                  className={cn(
                    'inline-flex gap-1 items-baseline',
                    current > limit ? 'text-red-600' : 'text-green-600',
                  )}
                >
                  <span className='font-semibold'>
                    {formatUtils.credits(data?.balance ?? 0, orgCurrency)}
                  </span>
                </span>
              </DisplayValue>
              <DisplayValue label={t`Monthly usage limit`}>
                <span
                  className={cn(
                    'inline-flex gap-1 items-baseline',
                    current > limit ? 'text-red-600' : 'text-green-600',
                  )}
                >
                  <span className='font-semibold'>
                    {formatUtils.credits(current, orgCurrency)}
                  </span>
                  /
                  <span className='text-xs font-semibold'>
                    {formatUtils.credits(limit, orgCurrency)}
                  </span>
                </span>
              </DisplayValue>
            </>
          )}
          <DisplayValue label={t`Billing Information`}>
            <div className='flex flex-col items-start gap-2'>
              {data?.billingInfo && (
                <OrganizationBillingInfo billingInfo={data.billingInfo} />
              )}
              <Button
                data-cta='admin-organization-form-billing-info-button'
                variant='secondary'
                size='sm'
                asChild
                icon={<Pencil size={16} />}
              >
                <Link
                  to='/organizations/$organizationId/billing'
                  params={{ organizationId: currentOrganizationId! }}
                >
                  {data?.billingInfo
                    ? t`Edit Billing Information`
                    : t`Set Billing Information`}
                </Link>
              </Button>
            </div>
          </DisplayValue>
        </MetaFields>
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
